import { useState, useEffect } from "react";
import { Flag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ReportMessageDialog({ messageId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [reasons, setReasons] = useState([]);

  useEffect(() => {
    if (!open || reasons.length) return;
    api.get("/report-reasons").then((r) => setReasons(r.data)).catch(() => {});
  }, [open, reasons.length]);

  const submit = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    setSending(true);
    try {
      await api.post(`/messages/${messageId}/report`, { reason, details: details.trim() || undefined });
      toast.success("Message reported");
      setOpen(false);
      setReason(""); setDetails("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button title="Report" className="hover:text-red-500" data-testid={`msg-report-${messageId}`}>
          <Flag className="w-3 h-3" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#FFFBF5] border-[#EBDCD1] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#3B2C24]">Report this message</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {reasons.map((r) => (
            <label
              key={r.key}
              className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition ${
                reason === r.key ? "border-[#FF7E67] bg-[#FF7E67]/5" : "border-[#EBDCD1] hover:bg-[#F9EFE6]"
              }`}
            >
              <input type="radio" name="msg-report-reason" checked={reason === r.key} onChange={() => setReason(r.key)} className="accent-[#FF7E67]" />
              <span className="text-sm text-[#3B2C24]">{r.label}</span>
            </label>
          ))}
        </div>
        <textarea
          className="cp-input min-h-[70px] resize-none text-sm"
          placeholder="Optional details…"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <button onClick={() => setOpen(false)} className="cp-btn-ghost">Cancel</button>
          <button onClick={submit} disabled={sending || !reason} className="cp-btn-primary disabled:opacity-50">
            {sending ? "Sending…" : "Send report"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
